import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  TrendUp, Receipt, CheckCircle, Users, CurrencyGbp, ClockClockwise, ArrowUpRight, Funnel, Bell,
} from "@phosphor-icons/react";
import { api, POUND } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATUS_CLASS = {
  Draft: "badge-draft",
  Sent: "badge-sent",
  Accepted: "badge-accepted",
  "In Progress": "badge-progress",
  Completed: "badge-completed",
  Rejected: "badge-rejected",
};

function Kpi({ label, value, hint, icon: Icon, accent }) {
  return (
    <div className="card-elev p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div className="overline">{label}</div>
        <div className={`w-9 h-9 grid place-items-center rounded-sm ${accent}`}>
          <Icon size={16} weight="bold" />
        </div>
      </div>
      <div className="mt-4 kpi-num text-[2.4rem] text-slate-900">{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-500 font-mono-data">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/stats");
        setStats(data);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="p-6 lg:p-10 max-w-[1400px]">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <div>
          <span className="hero-rule" />
          <div className="overline">Overview</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-2">
            Welcome back, {user?.name?.split(" ")[0] || "Roofer"}.
          </h1>
          <p className="text-sm text-slate-500 mt-2">Here&rsquo;s what&rsquo;s on the slate today.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/customers" className="btn-secondary" data-testid="dashboard-new-customer">+ Customer</Link>
          <Link to="/quotes/new" className="btn-primary flex items-center gap-2" data-testid="dashboard-new-quote">
            <ArrowUpRight size={16} weight="bold" /> New Quote
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Quotes Created" value={loading ? "—" : stats?.quotes_created ?? 0} icon={Receipt} accent="bg-blue-50 text-blue-700" />
        <Kpi label="Accepted" value={loading ? "—" : stats?.quotes_accepted ?? 0} hint={`${stats?.by_status?.Accepted || 0} live`} icon={CheckCircle} accent="bg-emerald-50 text-emerald-700" />
        <Kpi label="Jobs Completed" value={loading ? "—" : stats?.jobs_completed ?? 0} icon={TrendUp} accent="bg-amber-50 text-amber-700" />
        <Kpi label="Customers" value={loading ? "—" : stats?.customers_count ?? 0} icon={Users} accent="bg-slate-100 text-slate-700" />
      </div>

      {/* CRM KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <Kpi
          label="New Leads (mo)"
          value={loading ? "—" : stats?.new_leads_this_month ?? 0}
          hint="this calendar month"
          icon={Funnel}
          accent="bg-violet-50 text-violet-700"
        />
        <Kpi
          label="Conversion Rate"
          value={loading ? "—" : `${stats?.conversion_rate_pct ?? 0}%`}
          hint="quotes won / sent"
          icon={TrendUp}
          accent="bg-emerald-50 text-emerald-700"
        />
        <Kpi
          label="Follow-ups Due"
          value={loading ? "—" : stats?.followups_pending ?? 0}
          hint={`${stats?.followups_overdue ?? 0} overdue`}
          icon={Bell}
          accent="bg-amber-50 text-amber-700"
        />
        <Kpi
          label="Pipeline £"
          value={loading ? "—" : POUND(stats?.pipeline_value || 0)}
          hint="sent + accepted + in-progress"
          icon={CurrencyGbp}
          accent="bg-blue-50 text-blue-700"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mt-4">
        <div className="card-elev p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">Pipeline</div>
              <h3 className="font-display text-xl font-semibold mt-1">Quotes by Status</h3>
            </div>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-5">
            {["Draft", "Sent", "Accepted", "In Progress", "Completed", "Rejected"].map((s) => (
              <div key={s} className="border border-slate-200 p-3 rounded-sm">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{s}</div>
                <div className="font-mono-data text-xl font-bold mt-1">{stats?.by_status?.[s] ?? 0}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3 pt-4 border-t border-slate-200">
            <div>
              <div className="overline">Pipeline Value</div>
              <div className="font-mono-data text-2xl font-bold text-[var(--rqp-blue)] mt-1">{POUND(stats?.pipeline_value || 0)}</div>
            </div>
            <div>
              <div className="overline">Completed Revenue</div>
              <div className="font-mono-data text-2xl font-bold text-emerald-700 mt-1">{POUND(stats?.revenue_total || 0)}</div>
            </div>
          </div>
        </div>

        <div className="card-elev p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">Follow-ups</div>
              <h3 className="font-display text-xl font-semibold mt-1">Due next</h3>
            </div>
            <Bell size={20} className="text-amber-600" weight="bold" />
          </div>
          {(stats?.followups_upcoming || []).length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center border border-dashed border-slate-300 rounded-sm" data-testid="dashboard-no-followups">
              Nothing scheduled. <Link to="/pipeline" className="text-[var(--rqp-orange)] font-semibold underline">Schedule one</Link>.
            </div>
          ) : (
            <ul className="space-y-2" data-testid="dashboard-followups-list">
              {stats.followups_upcoming.map((f) => {
                const overdue = f.due_at && new Date(f.due_at) < new Date();
                return (
                  <li
                    key={f.id}
                    className={`flex items-start justify-between p-2.5 rounded-sm border ${overdue ? "border-rose-200 bg-rose-50" : "border-slate-200"}`}
                    data-testid={`dashboard-followup-${f.id}`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <div className="text-sm font-semibold truncate">{f.title || `${f.type} — ${f.customer_name || ""}`}</div>
                        {f.auto && (
                          <span className="text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 rounded-sm border bg-violet-50 text-violet-700 border-violet-200" title={`Auto-scheduled on ${f.auto_status}`}>
                            Auto
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate">{f.customer_name}{f.customer_postcode ? ` · ${f.customer_postcode}` : ""}</div>
                    </div>
                    <div className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-1 rounded-sm border ml-2 shrink-0 ${overdue ? "bg-rose-100 text-rose-700 border-rose-300" : "bg-slate-100 text-slate-700 border-slate-300"}`}>
                      {f.due_at?.slice(5, 10) || "—"}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <Link to="/pipeline" className="btn-secondary w-full mt-4 inline-block text-center" data-testid="dashboard-open-pipeline">Open pipeline →</Link>
        </div>

        <div className="card-elev p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">Quick start</div>
              <h3 className="font-display text-xl font-semibold mt-1">Get going</h3>
            </div>
            <CurrencyGbp size={20} className="text-[var(--rqp-orange)]" weight="bold" />
          </div>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2.5"><span className="w-5 h-5 grid place-items-center bg-[var(--rqp-orange)] text-white font-mono-data text-[11px] rounded-sm shrink-0">1</span><div>Add a customer with their site address &amp; photos.</div></li>
            <li className="flex items-start gap-2.5"><span className="w-5 h-5 grid place-items-center bg-[var(--rqp-orange)] text-white font-mono-data text-[11px] rounded-sm shrink-0">2</span><div>Pick a roof type and enter site measurements.</div></li>
            <li className="flex items-start gap-2.5"><span className="w-5 h-5 grid place-items-center bg-[var(--rqp-orange)] text-white font-mono-data text-[11px] rounded-sm shrink-0">3</span><div>Pick materials, set labour &amp; profit; export PDF.</div></li>
          </ul>
          <Link to="/quotes/new" className="btn-primary w-full mt-5 inline-block text-center" data-testid="dashboard-start-quote">Start a Quote</Link>
        </div>
      </div>

      <div className="card-elev p-5 mt-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="overline">Recent activity</div>
            <h3 className="font-display text-xl font-semibold mt-1">Latest quotes</h3>
          </div>
          <ClockClockwise size={18} className="text-slate-400" weight="bold" />
        </div>
        {!stats?.recent_activity?.length ? (
          <div className="text-sm text-slate-500 py-6 text-center border border-dashed border-slate-300 rounded-sm">
            No quotes yet. <Link to="/quotes/new" className="text-[var(--rqp-orange)] font-semibold underline">Create your first one</Link>.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="dashboard-recent-table">
              <thead>
                <tr className="text-left border-b border-slate-200">
                  <th className="overline pb-2 pr-3">Quote</th>
                  <th className="overline pb-2 pr-3">Customer</th>
                  <th className="overline pb-2 pr-3">Status</th>
                  <th className="overline pb-2 pr-3 text-right">Total</th>
                  <th className="overline pb-2"></th>
                </tr>
              </thead>
              <tbody className="font-mono-data">
                {stats.recent_activity.map((q) => (
                  <tr key={q.id} className="border-b border-slate-100">
                    <td className="py-3 pr-3">{q.quote_number}</td>
                    <td className="py-3 pr-3 font-sans">{q.customer_name}</td>
                    <td className="py-3 pr-3"><span className={`${STATUS_CLASS[q.status] || "badge-draft"} px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm`}>{q.status}</span></td>
                    <td className="py-3 pr-3 text-right font-semibold">{POUND(q.total)}</td>
                    <td className="py-3 text-right"><Link to={`/quotes/${q.id}`} className="text-[var(--rqp-orange)] text-xs font-semibold hover:underline">Open ↗</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
